import { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, Lock, Download, LogOut, KeyRound, Copy, MessageSquarePlus, Trash2, LayoutDashboard, RefreshCw } from "lucide-react";
import BookCover from "@/components/landing/BookCover";
import { ProgressTracker } from "@/components/member/ProgressTracker";
import { useAuth, API, formatApiErrorDetail } from "@/context/AuthContext";

const EASE = [0.16, 1, 0.3, 1];
const OWNER_EMAIL = "bathilysamir22@gmail.com";

const TRACKER_TITLES = {
  memorisation: "Guide de mémorisation — 6 mois",
  revision: "Guide de révision — 30 jours",
};

const EBOOKS = [
  { id: "memorisation", cover: "mem", title: "Guide de mémorisation", sub: "Le plan de 6 mois" },
  { id: "revision", cover: "rev", title: "Guide de révision", sub: "Le plan de 30 jours" },
  { id: "pack-complet", cover: null, title: "Pack Gold", sub: "Mémorisation + révision" },
];

export default function MemberPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [owned, setOwned] = useState([]);
  const [code, setCode] = useState("");
  const [activating, setActivating] = useState(false);
  const [genProduct, setGenProduct] = useState("pack-complet");
  const [lastCode, setLastCode] = useState("");
  const [testis, setTestis] = useState([]);
  const [tName, setTName] = useState("");
  const [tTag, setTTag] = useState("Guide mémorisation");
  const [tText, setTText] = useState("");
  const [tSaving, setTSaving] = useState(false);
  const [dash, setDash] = useState(null);
  const [prog, setProg] = useState([]);

  useEffect(() => {
    if (user === false) navigate("/connexion");
  }, [user, navigate]);

  useEffect(() => {
    if (!user) return;
    axios
      .get(`${API}/ebooks/my`, { withCredentials: true })
      .then((r) => setOwned(r.data.ebooks || []))
      .catch(() => setOwned([]));
    axios
      .get(`${API}/progress`, { withCredentials: true })
      .then((r) => setProg(r.data.progress || []))
      .catch(() => setProg([]));
  }, [user, location.key]);

  const activate = async (e) => {
    e.preventDefault();
    setActivating(true);
    try {
      const { data } = await axios.post(`${API}/ebooks/activate`, { code }, { withCredentials: true });
      toast.success("Ebook débloqué !", { description: data.message });
      setCode("");
      const r = await axios.get(`${API}/ebooks/my`, { withCredentials: true });
      setOwned(r.data.ebooks || []);
    } catch (err) {
      toast.error("Code invalide", { description: formatApiErrorDetail(err.response?.data?.detail) });
    } finally {
      setActivating(false);
    }
  };

  const generate = async () => {
    try {
      const { data } = await axios.post(`${API}/codes/generate`, { product: genProduct }, { withCredentials: true });
      setLastCode(data.code);
      toast.success("Code généré", { description: `Pour « ${data.title} » — envoie-le à ton client.` });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const copyCode = () => {
    navigator.clipboard?.writeText(lastCode);
    toast.success("Code copié !");
  };

  const loadTestis = () => {
    axios
      .get(`${API}/testimonials`, { withCredentials: true })
      .then((r) => setTestis(r.data.testimonials || []))
      .catch(() => {});
  };

  const loadDash = () => {
    axios
      .get(`${API}/vendor/dashboard`, { withCredentials: true })
      .then((r) => setDash(r.data))
      .catch(() => setDash(null));
  };

  useEffect(() => {
    if (user && user.email === OWNER_EMAIL) {
      loadTestis();
      loadDash();
    }
  }, [user]);

  const addTesti = async (e) => {
    e.preventDefault();
    setTSaving(true);
    try {
      await axios.post(`${API}/testimonials`, { name: tName, tag: tTag, text: tText }, { withCredentials: true });
      toast.success("Avis ajouté — il remplace les exemples sur la page d'accueil !");
      setTName("");
      setTText("");
      loadTestis();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setTSaving(false);
    }
  };

  const delTesti = async (id) => {
    try {
      await axios.delete(`${API}/testimonials/${id}`, { withCredentials: true });
      toast.success("Avis supprimé");
      loadTestis();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const download = (id) => {
    window.open(`${API}/ebooks/download/${id}`, "_blank");
  };

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  if (!user) {
    return (
      <div className="info-page member-loading" data-testid="member-loading">
        <p>Chargement de ton espace…</p>
      </div>
    );
  }

  const displayName = user.prenom || user.name || user.email;
  const isOwner = user.email === OWNER_EMAIL;

  return (
    <div className="info-page" data-testid="member-page">
      <header className="info-header">
        <Link to="/" className="info-back" data-testid="back-home-link">
          <ArrowLeft size={16} />
          Retour à l'accueil
        </Link>
        <button className="info-back logout-btn" onClick={handleLogout} data-testid="logout-btn">
          <LogOut size={16} />
          Se déconnecter
        </button>
      </header>

      <div className="container member-wrap">
        <motion.div initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, ease: EASE }}>
          <div className="eyebrow">Ton espace</div>
          <h1 className="member-title">Assalamu alaykum, {displayName}</h1>
          <p className="guides-lede">Retrouve ici tes guides débloqués et active tes nouveaux achats avec ton code.</p>
        </motion.div>

        <motion.div
          className="activate-panel"
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.15, ease: EASE }}
        >
          <div className="activate-head">
            <KeyRound size={18} />
            <b>J'ai un code d'activation</b>
          </div>
          <p>Tu l'as reçu après ton achat sur la boutique. Colle-le ici pour débloquer ton ebook.</p>
          <form onSubmit={activate} className="activate-row">
            <input
              type="text"
              placeholder="Ex. SN-XXXX-XXXX"
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              required
              data-testid="activation-code-input"
            />
            <button type="submit" className="btn" disabled={activating} data-testid="activation-submit-btn">
              {activating ? "Vérification…" : "Activer"}
            </button>
          </form>
        </motion.div>

        {isOwner && (
          <motion.div
            className="activate-panel owner-panel"
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.2, ease: EASE }}
            data-testid="vendor-dashboard"
          >
            <div className="activate-head owner-head">
              <LayoutDashboard size={18} />
              <b>Tableau de bord — commandes</b>
            </div>
            <p>Toutes les commandes Shopify arrivent ici automatiquement, avec le code d'activation envoyé au client.</p>
            {!dash ? (
              <p className="dash-empty">Chargement…</p>
            ) : (
              <>
                <div className="kpi-row">
                  <div className="kpi-card" data-testid="stat-orders"><b>{dash.stats.orders}</b><span>commandes</span></div>
                  <div className="kpi-card" data-testid="stat-revenue"><b>{dash.stats.revenue.toLocaleString("fr-FR")} €</b><span>valeur totale</span></div>
                  <div className="kpi-card" data-testid="stat-codes-used"><b>{dash.stats.codes_used}</b><span>codes activés</span></div>
                  <div className="kpi-card" data-testid="stat-members"><b>{dash.stats.members}</b><span>membres</span></div>
                </div>
                {dash.orders.length === 0 ? (
                  <p className="dash-empty" data-testid="dash-empty">Aucune commande pour le moment — elles apparaîtront ici automatiquement après chaque achat Shopify.</p>
                ) : (
                  <div className="order-list" data-testid="order-list">
                    {dash.orders.map((o) => (
                      <div className="order-row" key={o.order_id} data-testid={`order-row-${o.order_id}`}>
                        <div className="order-main">
                          <b>{o.product_title}</b>
                          <span className="order-email">{o.email}</span>
                        </div>
                        <div className="order-meta">
                          <span className="order-code">{o.code}</span>
                          <span className={`pill ${o.code_used ? "ok" : "wait"}`}>{o.code_used ? "activé" : "en attente"}</span>
                          <span className={`pill ${o.email_sent ? "ok" : "ko"}`}>{o.email_sent ? "email ✓" : "email ✗"}</span>
                          <span className="order-date">
                            {o.date ? new Date(o.date).toLocaleString("fr-FR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "—"}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                <div className="dash-actions">
                  <Link to="/proprietaire" className="btn dash-owner-link" data-testid="dashboard-owner-link">
                    Espace propriétaire →
                  </Link>
                  <button type="button" className="copy-btn dash-refresh" onClick={loadDash} data-testid="dash-refresh-btn">
                    <RefreshCw size={14} />
                    Actualiser
                  </button>
                </div>
              </>
            )}
          </motion.div>
        )}

        {isOwner && (
          <motion.div
            className="activate-panel owner-panel"
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.25, ease: EASE }}
            data-testid="owner-panel"
          >
            <div className="activate-head owner-head">
              <KeyRound size={18} />
              <b>Espace vendeur — générer un code</b>
            </div>
            <p>Après chaque vente sur Shopify, le client reçoit son code automatiquement par email (tu peux aussi en générer un ici à la main si besoin). Chaque code débloque uniquement le produit payé.</p>
            <div className="activate-row">
              <select value={genProduct} onChange={(e) => setGenProduct(e.target.value)} data-testid="owner-product-select">
                <option value="memorisation">Guide de mémorisation</option>
                <option value="revision">Guide de révision</option>
                <option value="pack-complet">Pack Gold</option>
              </select>
              <button className="btn" onClick={generate} data-testid="owner-generate-btn">
                Générer un code
              </button>
            </div>
            {lastCode && (
              <div className="owner-code" data-testid="owner-generated-code">
                <b>{lastCode}</b>
                <button className="copy-btn" onClick={copyCode} data-testid="copy-code-btn">
                  <Copy size={14} />
                  Copier
                </button>
              </div>
            )}
          </motion.div>
        )}

        {isOwner && (
          <motion.div
            className="activate-panel owner-panel"
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.35, ease: EASE }}
            data-testid="testimonials-panel"
          >
            <div className="activate-head owner-head">
              <MessageSquarePlus size={18} />
              <b>Témoignages de la page d'accueil</b>
            </div>
            <p>Ajoute ici les vrais avis de tes clientes et clients : dès le premier ajout, ils remplacent automatiquement les exemples dans la section « Ils l'ont fait ».</p>
            <form onSubmit={addTesti} className="testi-form">
              <div className="activate-row">
                <input
                  type="text"
                  placeholder="Prénom + initiale (ex. Amina L.)"
                  value={tName}
                  onChange={(e) => setTName(e.target.value)}
                  required
                  data-testid="testi-name-input"
                />
                <select value={tTag} onChange={(e) => setTTag(e.target.value)} data-testid="testi-tag-select">
                  <option>Guide mémorisation</option>
                  <option>Guide révision</option>
                  <option>Pack Gold</option>
                </select>
              </div>
              <textarea
                placeholder="Son avis (2-3 phrases)"
                rows={3}
                value={tText}
                onChange={(e) => setTText(e.target.value)}
                required
                data-testid="testi-text-input"
              />
              <button type="submit" className="btn" disabled={tSaving} data-testid="testi-add-btn">
                {tSaving ? "Ajout…" : "Ajouter l'avis"}
              </button>
            </form>
            {testis.length > 0 && (
              <div className="testi-list" data-testid="testi-list">
                {testis.map((t) => (
                  <div className="testi-item" key={t.id} data-testid={`testi-item-${t.id}`}>
                    <div>
                      <b>{t.name}</b>
                      <span className="testi-tag">{t.tag}</span>
                      <p>{t.text}</p>
                    </div>
                    <button type="button" className="copy-btn" onClick={() => delTesti(t.id)} data-testid={`testi-del-${t.id}`}>
                      <Trash2 size={14} />
                      Supprimer
                    </button>
                  </div>
                ))}
              </div>
            )}
          </motion.div>
        )}

        <div className="ebooks-grid">
          {EBOOKS.map((b, i) => {
            const isOwned = owned.includes(b.id);
            return (
              <motion.div
                key={b.id}
                className={`ebook-card ${isOwned ? "owned" : "locked"}`}
                initial={{ opacity: 0, y: 50 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.9, delay: 0.25 + i * 0.1, ease: EASE }}
                data-testid={`ebook-card-${b.id}`}
              >
                <div className="ebook-cover">
                  {b.cover ? <BookCover variant={b.cover} mini /> : (
                    <div className="ebook-duo">
                      <BookCover variant="mem" mini />
                      <BookCover variant="rev" mini />
                    </div>
                  )}
                  {!isOwned && <div className="ebook-lock"><Lock size={20} /></div>}
                </div>
                <b>{b.title}</b>
                <span className="ebook-sub">{b.sub}</span>
                {isOwned ? (
                  b.id === "pack-complet" ? (
                    <span className="ebook-state owned-state" data-testid="owned-pack-complet">Débloqué — télécharge les 2 guides</span>
                  ) : (
                    <button className="btn ebook-dl" onClick={() => download(b.id)} data-testid={`download-${b.id}-btn`}>
                      <Download size={15} />
                      Télécharger le PDF
                    </button>
                  )
                ) : (
                  <span className="ebook-state" data-testid={`locked-${b.id}`}>Verrouillé — active ton code</span>
                )}
              </motion.div>
            );
          })}
        </div>

        {prog.length > 0 && (
          <motion.div
            className="progress-section"
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.3, ease: EASE }}
            data-testid="progress-section"
          >
            <div className="eyebrow">Ta progression</div>
            <h2 className="progress-title">Suis ta mémorisation, jour après jour</h2>
            <div className="progress-list">
              {prog.map((p) => (
                <ProgressTracker key={p.product} data={{ ...p, title: TRACKER_TITLES[p.product] }} />
              ))}
            </div>
          </motion.div>
        )}
      </div>

      <footer className="info-footer">
        <div className="copyright">
          © SamNara — Guides numériques, livraison instantanée après achat
        </div>
      </footer>
    </div>
  );
}
